import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type AddonPurchase = {
  id: number;
  authChurch: string;
  addonType: string;
  quantity: string;
  amount: number;
  purchasedAt: string;
  note: string;
  createdBy: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_FORM = { authChurch: '', addonType: '', quantity: '', amount: '', purchasedAt: today(), note: '' };

export default function AddonPurchases() {
  const session = getAdminSession();
  const [items, setItems] = useState<AddonPurchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<AddonPurchase[]>>(`${MainURL}/admin-console/addon-purchases`);
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '구매 내역을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '구매 내역을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    if (!form.authChurch.trim() || !form.addonType.trim() || !form.purchasedAt) {
      alert('교회, 옵션 종류, 구매일을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/addon-purchases`, {
        requesterId: session.id,
        ...form,
        amount: Number(form.amount) || 0,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      setForm(EMPTY_FORM);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '등록하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 내역을 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/addon-purchases/${id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">부가 옵션 구매 내역</h2>
          <p className="admin-panel-desc">알림톡, 용량 추가 등 부가 옵션 구매 및 충전 내역을 관리합니다.</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="authChurch"
          value={form.authChurch}
          onChange={(e) => setForm((prev) => ({ ...prev, authChurch: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <input
          type="text"
          placeholder="옵션 종류 (예: 알림톡, 용량추가)"
          value={form.addonType}
          onChange={(e) => setForm((prev) => ({ ...prev, addonType: e.target.value }))}
          style={{ maxWidth: 200 }}
        />
        <input
          type="text"
          placeholder="수량/용량"
          value={form.quantity}
          onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
          style={{ maxWidth: 120 }}
        />
        <input
          type="number"
          placeholder="금액"
          value={form.amount}
          onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
          style={{ maxWidth: 120 }}
        />
        <input
          type="date"
          value={form.purchasedAt}
          onChange={(e) => setForm((prev) => ({ ...prev, purchasedAt: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <input
          type="text"
          placeholder="비고"
          value={form.note}
          onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
        />
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            등록
          </button>
        </div>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>구매일</th>
              <th>교회</th>
              <th>옵션</th>
              <th>수량</th>
              <th>금액</th>
              <th>비고</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={7}>등록된 구매 내역이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{String(item.purchasedAt).slice(0, 10)}</td>
                  <td>{item.authChurch}</td>
                  <td>{item.addonType}</td>
                  <td>{item.quantity || '-'}</td>
                  <td>{item.amount.toLocaleString()}원</td>
                  <td>{item.note || '-'}</td>
                  <td>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item.id)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
